import type { UserClaims } from '@okta/okta-auth-js';

/**
 * User profile information extracted from Okta tokens
 */
export interface UserProfile {
  /** Unique user identifier (sub claim) */
  id: string;
  /** User's email address */
  email: string;
  /** User's full name */
  name?: string;
  /** User's given (first) name */
  given_name?: string;
  /** User's family (last) name */
  family_name?: string;
  /** User's preferred username */
  preferred_username?: string;
}

/**
 * Authentication error context for Sentry reporting
 */
export interface AuthErrorContext {
  /** The attempted username/email during authentication */
  attempted_username?: string;
  /** The authentication operation being performed */
  operation:
    | 'login'
    | 'logout'
    | 'token_refresh'
    | 'callback'
    | 'get_user_profile';
  /** Current authentication state at time of error */
  auth_state?: {
    isAuthenticated?: boolean;
    isPending?: boolean;
    error?: string;
  };
  /** Additional context specific to the auth operation */
  additional_context?: Record<string, unknown>;
}

/**
 * Convert Okta UserClaims to application UserProfile interface.
 *
 * Transforms the raw user claims from Okta ID tokens into a standardized
 * UserProfile object used throughout the application. Handles optional
 * fields and provides fallback values where appropriate.
 *
 * @param claims - User claims object from Okta ID token
 * @param claims.sub - Subject claim (unique user identifier)
 * @param claims.email - User's email address (optional in claims)
 * @param claims.name - User's full display name
 * @param claims.given_name - User's first name
 * @param claims.family_name - User's last name
 * @param claims.preferred_username - User's preferred username
 *
 * @returns UserProfile object with standardized application fields
 *
 * @example
 * ```typescript
 * // Transform Okta claims to user profile
 * const claims: UserClaims = {
 *   sub: 'okta-user-123',
 *   email: 'john.doe@company.com',
 *   name: 'John Doe',
 *   given_name: 'John',
 *   family_name: 'Doe',
 *   preferred_username: 'john.doe'
 * };
 *
 * const userProfile = oktaClaimsToUserProfile(claims);
 * // Returns:
 * // {
 * //   id: 'okta-user-123',
 * //   email: 'john.doe@company.com',
 * //   name: 'John Doe',
 * //   given_name: 'John',
 * //   family_name: 'Doe',
 * //   preferred_username: 'john.doe'
 * // }
 * ```
 */
export function oktaClaimsToUserProfile(claims: UserClaims): UserProfile {
  return {
    id: claims.sub,
    email: claims.email || '',
    name: claims.name,
    given_name: claims.given_name,
    family_name: claims.family_name,
    preferred_username: claims.preferred_username,
  };
}
